import { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, Link, Paper, Stack, Table, TableBody, TableCell, TableHead,
  TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { formatMinor } from '@eprintsmart/shared';
import { getPaymentOps, resolvePendingRefund, type PaymentOps, type QueuedRefund } from '../services/payments';

/**
 * Where the money is.
 *
 * This page exists because on 24 Sep 2026 a ₹330 refund had failed 93 times
 * over four days and nothing in this console could say why. The balance it is
 * paid from was invisible, the refund-credit pool was invisible, and the order
 * page confidently told staff to "add refund credits… and it goes out
 * automatically within the hour" — which was wrong on both counts.
 */
export function Money() {
  const [data, setData] = useState<PaymentOps | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [resolving, setResolving] = useState<{ refund: QueuedRefund; action: 'retry' | 'cancel' } | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await getPaymentOps());
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load payment operations.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  async function submitResolve() {
    if (!resolving) return;
    setBusy(true);
    try {
      await resolvePendingRefund({
        orderId: resolving.refund.orderId,
        action: resolving.action,
        note: reason.trim(),
      });
      setNote(resolving.action === 'retry'
        ? `Refund on ${resolving.refund.orderNumber} is back in the queue.`
        : `Refund on ${resolving.refund.orderNumber} was cancelled. No money was paid.`);
      setResolving(null);
      setReason('');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  }

  const balance = data?.balance;

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <Typography variant="h1" sx={{ flexGrow: 1 }}>Money</Typography>
        <Button size="small" onClick={() => void load()} disabled={loading}>Refresh</Button>
      </Stack>

      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
      {note && <Alert severity="success" onClose={() => setNote(null)}>{note}</Alert>}

      {loading ? (
        <Box sx={{ display: 'grid', placeItems: 'center', py: 6 }}><CircularProgress /></Box>
      ) : !data ? null : (
        <>
          {/* A failed balance read renders as "unknown", never as ₹0. A false
              zero is the worst possible answer on a screen about whether a
              refund can go out. */}
          {balance && !balance.available && (
            <Alert severity="warning">
              Razorpay would not tell us the balance just now, so the figures below are unknown
              rather than zero. {balance.error}
            </Alert>
          )}

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
            <Stat
              label="Razorpay balance"
              value={balance?.available ? formatMinor(balance.balanceMinor ?? 0) : 'unknown'}
              note="what a refund is paid from"
              tone={balance?.available && (balance.balanceMinor ?? 0) === 0 ? 'warning' : undefined}
            />
            <Stat
              label="Refund credits"
              value={balance?.available ? formatMinor(balance.refundCreditsMinor ?? 0) : 'unknown'}
              note="a separate pool Razorpay may not draw on"
            />
            <Stat
              label="Queued refunds"
              value={String(data.queued.length)}
              note={data.queued.length ? 'waiting on funds or a person' : 'nothing stuck'}
              tone={data.queued.length ? 'warning' : undefined}
            />
            <Stat
              label="Open disputes"
              value={String(data.disputes.length)}
              note="chargebacks"
              tone={data.disputes.length ? 'warning' : undefined}
            />
          </Box>

          <Alert severity="info">{data.note}</Alert>

          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Refunds that have not gone out</Typography>
            <Divider sx={{ mb: 1.5 }} />
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Order</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>State</TableCell>
                  <TableCell align="right">Attempts</TableCell>
                  <TableCell>Why it is stuck</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.queued.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      No refund is waiting. Every one that was asked for has been paid.
                    </TableCell>
                  </TableRow>
                ) : data.queued.map((r) => (
                  <TableRow key={r.orderId}>
                    <TableCell>
                      <Link component={RouterLink} to={`/orders/${r.orderId}`}>{r.orderNumber}</Link>
                      <Typography variant="caption" display="block" color="text.secondary">
                        asked {new Date(r.requestedAt).toLocaleDateString('en-IN')} · {r.reason}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{formatMinor(r.amountMinor)}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        variant="outlined"
                        color={r.status === 'FAILED' ? 'error' : 'warning'}
                        label={r.status === 'FAILED' ? 'Needs a person' : 'Waiting for funds'}
                      />
                    </TableCell>
                    <TableCell align="right">{r.attempts}</TableCell>
                    <TableCell sx={{ maxWidth: 380 }}>
                      {/* The single fact that was missing through all 93
                          attempts: would the balance we can see cover it? */}
                      {r.coveredByBalance === true && (
                        <Chip size="small" color="success" variant="outlined" label="Balance covers it — next sweep should pay it" />
                      )}
                      {r.coveredByBalance === false && (
                        <Chip size="small" color="error" variant="outlined" label="Balance does not cover it" />
                      )}
                      <Tooltip title={r.lastError ?? ''}>
                        <Typography variant="caption" display="block" color="text.secondary" noWrap>
                          {r.lastError ?? 'no error recorded'}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        {r.status === 'FAILED' && (
                          <Button size="small" onClick={() => { setResolving({ refund: r, action: 'retry' }); setReason(''); }}>
                            Retry
                          </Button>
                        )}
                        <Button size="small" color="error" onClick={() => { setResolving({ refund: r, action: 'cancel' }); setReason(''); }}>
                          Cancel
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>

          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Recent Razorpay deliveries</Typography>
            <Divider sx={{ mb: 1.5 }} />
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Received</TableCell>
                  <TableCell>Event</TableCell>
                  <TableCell>Outcome</TableCell>
                  <TableCell align="right">Attempts</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.recentEvents.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      Nothing yet. Events appear here from the next payment onwards.
                    </TableCell>
                  </TableRow>
                ) : data.recentEvents.map((e) => (
                  <TableRow key={e.id}>
                    <TableCell>{new Date(e.receivedAt).toLocaleString('en-IN')}</TableCell>
                    <TableCell>{e.event}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        variant="outlined"
                        color={e.status === 'PROCESSED' ? 'success' : e.status === 'FAILED' ? 'error' : 'default'}
                        label={e.status === 'IGNORED' ? 'nothing to do' : e.status.toLowerCase()}
                      />
                      {e.error && (
                        <Typography variant="caption" display="block" color="error">{e.error}</Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">{e.attempts}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}

      <Dialog open={!!resolving} onClose={() => !busy && setResolving(null)} fullWidth maxWidth="sm">
        <DialogTitle>
          {resolving?.action === 'retry' ? 'Put this refund back in the queue' : 'Cancel this queued refund'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {resolving?.action === 'retry' ? (
              <Alert severity="info">
                This does not call Razorpay itself. It moves the refund of{' '}
                <strong>{resolving && formatMinor(resolving.refund.amountMinor)}</strong> back into the
                queue, and the next capture or hourly sweep will try to pay it.
              </Alert>
            ) : (
              <Alert severity="warning">
                This removes the refund of{' '}
                <strong>{resolving && formatMinor(resolving.refund.amountMinor)}</strong> from the queue.
                No money has been paid and none will be. If the customer was told a refund was
                coming, they need to hear from you.
              </Alert>
            )}
            <TextField
              label="Why"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              multiline
              minRows={2}
              helperText="Recorded on the order's history and in the audit log."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResolving(null)} disabled={busy}>Close</Button>
          <Button
            variant="contained"
            color={resolving?.action === 'cancel' ? 'error' : 'primary'}
            disabled={busy || reason.trim().length < 3}
            onClick={() => void submitResolve()}
          >
            {resolving?.action === 'retry' ? 'Queue it again' : 'Cancel the refund'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

function Stat({ label, value, note, tone }: { label: string; value: string; note?: string; tone?: 'warning' }) {
  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography
        variant="h2"
        sx={{ fontSize: '1.5rem', mt: 0.5, color: tone === 'warning' ? 'warning.main' : 'text.primary' }}
      >
        {value}
      </Typography>
      {note && <Typography variant="caption" color="text.secondary">{note}</Typography>}
    </Paper>
  );
}
